import React, { useState, useEffect } from 'react';
import { phoneBrandsApi, formatApiErrorDetail } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Smartphone, Plus, Loader2, Edit2, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';

export default function PhoneBrandsPage() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandName, setBrandName] = useState('');
  const [brandOs, setBrandOs] = useState('ANDROID');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchBrands = async () => {
    try {
      const { data } = await phoneBrandsApi.listAll();
      setBrands(data);
    } catch (error) {
      toast.error('Failed to load brands');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchBrands(); }, []);

  const handleSave = async () => {
    if (!brandName.trim()) { toast.error('Brand name required'); return; }
    setSaving(true);
    try {
      if (editingBrand) {
        await phoneBrandsApi.update(editingBrand.id, { name: brandName.trim(), os_type: brandOs });
        toast.success('Brand updated');
      } else {
        await phoneBrandsApi.create({ name: brandName.trim(), os_type: brandOs });
        toast.success('Brand created');
      }
      setShowDialog(false);
      setBrandName('');
      setBrandOs('ANDROID');
      setEditingBrand(null);
      fetchBrands();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to save brand');
    } finally { setSaving(false); }
  };

  const handleToggle = async (brand) => {
    try {
      await phoneBrandsApi.update(brand.id, { is_active: !brand.is_active });
      toast.success(brand.is_active ? 'Brand deactivated' : 'Brand activated');
      fetchBrands();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (brand) => {
    if (!window.confirm(`Delete ${brand.name}?`)) return;
    try {
      await phoneBrandsApi.delete(brand.id);
      toast.success('Brand deleted');
      fetchBrands();
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = brands.filter(b => b.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }} data-testid="phone-brands-heading">Phone Brands</h1>
            <p className="text-slate-600 mt-1">Manage device brands available in the platform</p>
          </div>
          <Button onClick={() => { setEditingBrand(null); setBrandName(''); setBrandOs('ANDROID'); setShowDialog(true); }} className="bg-[#005DE8] hover:bg-[#0047B3]" data-testid="add-brand-btn">
            <Plus className="h-4 w-4 mr-2" />Add Brand
          </Button>
        </div>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search brands..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-11" data-testid="search-brands" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" /></div>
            ) : filtered.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Brand Name</TableHead>
                    <TableHead>OS</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(brand => (
                    <TableRow key={brand.id} className="table-row-hover">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Smartphone className="h-4 w-4 text-slate-400" />
                          <span className="font-medium text-slate-900">{brand.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={brand.os_type === 'IOS' ? 'bg-slate-800 text-white' : brand.os_type === 'ANDROID' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                          {brand.os_type || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={brand.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200 border' : 'bg-slate-100 text-slate-600 border-slate-200 border'}>
                          {brand.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{formatDateTime(brand.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => { setEditingBrand(brand); setBrandName(brand.name); setBrandOs(brand.os_type || 'ANDROID'); setShowDialog(true); }} className="h-8 w-8 p-0">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleToggle(brand)} className="h-8 text-xs px-2">
                            {brand.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(brand)} className="h-8 w-8 p-0 text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <Smartphone className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No brands found</h3>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingBrand ? 'Edit Brand' : 'Add Brand'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label className="text-sm">Brand Name</Label>
                <Input value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Brand name" data-testid="brand-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Operating System</Label>
                <Select value={brandOs} onValueChange={setBrandOs}>
                  <SelectTrigger data-testid="brand-os-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ANDROID">Android</SelectItem>
                    <SelectItem value="IOS">iOS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#005DE8]" data-testid="brand-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editingBrand ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
